import Link from 'next/link'
import { useRouter } from 'next/router'
import { useTranslation } from 'next-i18next'
import React, { useMemo } from 'react'

import EmptyState from '@/components/EmptyState'
import { getDetailContactList } from '@/services/sez-regions'
import { ListBasicRequest } from '@/types/sez-regions'
import { useAppSelector } from '@/utils/hooks'

const Contact: React.FC = () => {
  const router = useRouter()
  const { t } = useTranslation()
  const request: ListBasicRequest = useMemo(() => {
    return {
      params: { slug: String(router.query?.slug), skip: 0, limit: 1000 },
      headers: { 'Accept-Language': String(router.locale) },
    }
  }, [])
  const { data } = useAppSelector(getDetailContactList.select(request))
  return (
    <div className='container flex w-full flex-col gap-4 lg:gap-8'>
      <h5 className='text-lg font-bold leading-7 text-gray-1 lg:text-2xl lg:leading-8'>{t('common:contact')}</h5>

      <div className='flex flex-col gap-6'>
        {data && data.data.items.length > 0 ? (
          <>
            {data.data.items.map((item) => (
              <React.Fragment key={item.xid}>
                <div className='flex w-full flex-col gap-3 rounded-lg border border-neutral-border p-4'>
                  <p className='font-bold text-gray-1'>{item.title}</p>
                  {item.name && (
                    <div className='flex gap-2'>
                      <div className='shrink-0 basis-20'>
                        <p className='text-sm font-semibold tracking-[-0.084px] text-gray-1'>
                          {t('investment:distributionMaps.detail.contact.name')}
                        </p>
                      </div>
                      <p className='text-sm tracking-[-0.084px] text-gray-1'>:</p>
                      <p className='line-clamp-2 flex-1 text-sm tracking-[-0.084px] text-gray-1'>{item.name}</p>
                    </div>
                  )}

                  {item.address && (
                    <div className='flex gap-2'>
                      <div className='shrink-0 basis-20'>
                        <p className='text-sm font-semibold tracking-[-0.084px] text-gray-1'>
                          {t('investment:distributionMaps.detail.contact.address')}
                        </p>
                      </div>
                      <p className='text-sm tracking-[-0.084px] text-gray-1'>:</p>
                      <p className='line-clamp-2 flex-1 text-sm tracking-[-0.084px] text-gray-1'>{item.address}</p>
                    </div>
                  )}

                  {item.phoneNumber && (
                    <div className='flex gap-2'>
                      <div className='shrink-0 basis-20'>
                        <p className='text-sm font-semibold tracking-[-0.084px] text-gray-1'>
                          {t('investment:distributionMaps.detail.contact.phone')}
                        </p>
                      </div>
                      <p className='text-sm tracking-[-0.084px] text-gray-1'>:</p>
                      <Link
                        href={`tel:${item.phoneNumber}`}
                        target='_blank'
                        className='line-clamp-2 flex-1 text-sm tracking-[-0.084px] text-gray-1'
                      >
                        {item.phoneNumber}
                      </Link>
                    </div>
                  )}

                  {item.fax && (
                    <div className='flex gap-2'>
                      <div className='shrink-0 basis-20'>
                        <p className='text-sm font-semibold tracking-[-0.084px] text-gray-1'>
                          {t('investment:distributionMaps.detail.contact.fax')}
                        </p>
                      </div>
                      <p className='text-sm tracking-[-0.084px] text-gray-1'>:</p>
                      <p className='line-clamp-2 flex-1 text-sm tracking-[-0.084px] text-gray-1'>{item.fax}</p>
                    </div>
                  )}

                  {item.email && (
                    <div className='flex gap-2'>
                      <div className='shrink-0 basis-20'>
                        <p className='text-sm font-semibold tracking-[-0.084px] text-gray-1'>
                          {t('investment:distributionMaps.detail.contact.email')}
                        </p>
                      </div>
                      <p className='text-sm tracking-[-0.084px] text-gray-1'>:</p>
                      <div className='flex-1'>
                        <Link
                          href={`mailto:${item.email}`}
                          className='break-all text-sm tracking-[-0.084px] underline'
                        >
                          {item.email}
                        </Link>
                      </div>
                    </div>
                  )}
                </div>
              </React.Fragment>
            ))}
          </>
        ) : (
          <EmptyState
            title={String(t('common:emptyState.title'))}
            description={String(t('common:emptyState.description'))}
            contentWidth='lg:w-full'
          />
        )}
      </div>
    </div>
  )
}

export default Contact
